import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { cn } from "../../../utils";
import { ENTITY_KIND_ICONS } from "../../../constants/entity-icons";
import { Avatar, AvatarFallback, AvatarImage } from "../../base/avatar/avatar";
import { EntityMentionHoverCard } from "./entity-mention-hover-card";
/**
 * The chip's glyph per mentioned type, plus the `role` kind an OrgFunction
 * chip resolves into. Read off `ENTITY_KIND_ICONS` so a chip cannot show a
 * different glyph than the record's own detail page.
 */ const CHIP_ICONS = {
    user: ENTITY_KIND_ICONS.person,
    process: ENTITY_KIND_ICONS.process,
    team: ENTITY_KIND_ICONS.team,
    role: ENTITY_KIND_ICONS.role,
    system: ENTITY_KIND_ICONS.system,
    department: ENTITY_KIND_ICONS.department,
    artifact: ENTITY_KIND_ICONS.artifact,
    externalContact: ENTITY_KIND_ICONS.externalContact
};
// `not-prose`: the chip is a UI component that happens to sit inside the
// transcript's typography scope. Without the escape, `.prose img` hands the
// avatar/logo a 24px vertical margin, which pushes it clear out of its 16px
// `overflow-hidden` slot — the image loads, measures correctly, and paints
// nothing.
const CHIP_CLASS = cn("not-prose inline-flex items-center gap-1 px-2 py-0.5 rounded-full", "bg-muted hover:bg-muted/80 text-sm font-medium", "no-underline transition-colors");
/**
 * The record's own image in the chip's icon slot — the avatar for a person,
 * the logo for a system — with the type icon as the fallback Radix shows
 * until the image has actually loaded, and keeps on error.
 */ function ChipImage({ url, Icon, className, imageClassName }) {
    return /*#__PURE__*/ _jsxs(Avatar, {
        "data-slot": "chip-media",
        className: cn("h-4 w-4 shrink-0", className),
        children: [
            /*#__PURE__*/ _jsx(AvatarImage, {
                src: url,
                alt: "",
                className: imageClassName
            }),
            /*#__PURE__*/ _jsx(AvatarFallback, {
                className: "bg-transparent",
                children: /*#__PURE__*/ _jsx(Icon, {
                    className: "h-3.5 w-3.5"
                })
            })
        ]
    });
}
function ChipContent({ Icon, name, media }) {
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            media ?? /*#__PURE__*/ _jsx(Icon, {
                className: "h-3.5 w-3.5 shrink-0"
            }),
            /*#__PURE__*/ _jsx("span", {
                children: name
            })
        ]
    });
}
/**
 * The chip itself: the host's anchor, wrapped in the hover card for the four
 * types that have one.
 *
 * An OrgFunction chip stays a `team` to the card vocabulary whichever kind its
 * route turns out to be — both kinds share one collection and one card.
 */ function ChipLink({ data, type, id, slug, orgFunctionKind, Icon, name, className, media }) {
    const { EntityLink } = data;
    return /*#__PURE__*/ _jsx(EntityMentionHoverCard, {
        data: data,
        entityType: type,
        entityId: id,
        children: /*#__PURE__*/ _jsx(EntityLink, {
            type: type,
            id: id,
            slug: slug,
            orgFunctionKind: orgFunctionKind,
            className: cn(CHIP_CLASS, className),
            children: /*#__PURE__*/ _jsx(ChipContent, {
                Icon: Icon,
                name: name,
                media: media
            })
        })
    });
}
/**
 * A person's chip carries their avatar where the record has one. The users
 * lookup is the same cached org-wide list every picker shares — the same
 * single-list price the team chip already pays for its route kind — and the
 * type icon stands in while the list or the image is still on its way.
 */ function UserChipLink(props) {
    const avatarUrl = props.data.useUserAvatarUrl(props.id);
    return /*#__PURE__*/ _jsx(ChipLink, {
        ...props,
        type: "user",
        Icon: CHIP_ICONS.user,
        media: avatarUrl ? /*#__PURE__*/ _jsx(ChipImage, {
            url: avatarUrl,
            Icon: CHIP_ICONS.user,
            className: "rounded-full"
        }) : undefined
    });
}
/** The system counterpart: the logo, square and uncropped, over the same list. */ function SystemChipLink(props) {
    const logoUrl = props.data.useSystemLogoUrl(props.id);
    return /*#__PURE__*/ _jsx(ChipLink, {
        ...props,
        type: "system",
        Icon: CHIP_ICONS.system,
        media: logoUrl ? /*#__PURE__*/ _jsx(ChipImage, {
            url: logoUrl,
            Icon: CHIP_ICONS.system,
            className: "rounded-xs",
            imageClassName: "object-contain"
        }) : undefined
    });
}
/**
 * Chat's `team` entity type covers any OrgFunction, but the detail pages are
 * kind-fixed (/organization/teams vs …/functions), so the kind must be
 * resolved from the cached lookup list (includes soft-deleted entities).
 * Until it resolves the adapter answers `team` — the chat label says "team",
 * so that's the better guess.
 */ function OrgFunctionChipLink(props) {
    const kind = props.data.useOrgFunctionKind(props.id);
    return /*#__PURE__*/ _jsx(ChipLink, {
        ...props,
        type: "team",
        orgFunctionKind: kind,
        Icon: CHIP_ICONS[kind]
    });
}
/**
 * Compact inline entity card that renders as a clickable chip within text flow.
 * Shows the entity's image (avatar / logo) where the record has one, its type
 * icon otherwise, plus the name, and navigates to the detail page on click.
 * Designed to fit naturally within paragraph text rendered by ReactMarkdown.
 *
 * The label stays the model's write-time snapshot in every state — the record as
 * it is now lives in the hover card, for the four types that have one. The
 * image is the one exception, deliberately: it is decoration resolved from the
 * live record, not part of what the assistant said.
 */ export function InlineEntityCard({ type, ...rest }) {
    if (type === "team") return /*#__PURE__*/ _jsx(OrgFunctionChipLink, {
        ...rest
    });
    if (type === "user") return /*#__PURE__*/ _jsx(UserChipLink, {
        ...rest
    });
    if (type === "system") return /*#__PURE__*/ _jsx(SystemChipLink, {
        ...rest
    });
    return /*#__PURE__*/ _jsx(ChipLink, {
        ...rest,
        type: type,
        Icon: CHIP_ICONS[type]
    });
}

//# sourceMappingURL=inline-entity-card.js.map